/* Shopify applies CREATED_AT before pagination, so every page stays newest first.
 * https://shopify.dev/docs/api/storefront/2026-07/queries/products
 * Uses public tokenless product access; no Admin or private credentials.
 */
(() => {
  if (window.RaNewestSearch) return;
  const query = `query RaNewestProducts($query: String!, $first: Int, $last: Int, $after: String, $before: String, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
    products(first: $first, last: $last, after: $after, before: $before, query: $query, sortKey: CREATED_AT, reverse: true) {
      nodes { id handle title createdAt availableForSale onlineStoreUrl featuredImage { url altText width height } priceRange { minVariantPrice { amount currencyCode } } }
      pageInfo { hasNextPage hasPreviousPage endCursor startCursor }
    }
  }`;
  const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, char => ({'&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;'}[char]));
  function searchQuery(term) {
    // Keep matches in product names/categories/brands, not incidental words in descriptions.
    const words = term.toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) || [];
    const parts = words.map((word, index) => {
      const suffix = index === words.length - 1 ? '*' : '';
      return `(title:${word}${suffix} OR product_type:${word}${suffix} OR vendor:${word}${suffix})`;
    });
    parts.push('available_for_sale:true');
    return parts.join(' AND ');
  }
  async function products(term, config, page = {}) {
    if (!term.match(/[\p{L}\p{N}]/u)) return {nodes: [], pageInfo: {hasNextPage: false, hasPreviousPage: false}};
    const response = await fetch('/api/2026-07/graphql.json', {
      method: 'POST', headers: {'Content-Type': 'application/json'},
      signal: page.signal ? AbortSignal.any([page.signal, AbortSignal.timeout(15000)]) : AbortSignal.timeout(15000),
      body: JSON.stringify({query, variables: {
        query: searchQuery(term), first: page.before ? null : 12, last: page.before ? 12 : null,
        after: page.after || null, before: page.before || null,
        country: config.country || null, language: config.language || null
      }})
    });
    if (!response.ok) throw new Error('Search request failed');
    const result = await response.json();
    if (result.errors?.length || !result.data?.products) throw new Error('Search response unavailable');
    return {...result.data.products, nodes: result.data.products.nodes.filter(product => product.availableForSale)};
  }
  const arrivalsQuery = `query RaAvailableArrivals($handle: String!, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
    collection(handle: $handle) {
      products(first: 12, filters: [{available: true}], sortKey: COLLECTION_DEFAULT) {
        nodes { id handle title createdAt availableForSale onlineStoreUrl featuredImage { url altText width height } priceRange { minVariantPrice { amount currencyCode } } }
      }
    }
  }`;
  const arrivalsRequests = new WeakMap();
  async function refreshArrivals(discovery) {
    const grid = discovery?.querySelector('[data-available-arrivals]');
    if (!grid || arrivalsRequests.has(discovery) || Date.now() - Number(discovery.dataset.availableChecked || 0) < 30000) return;
    const config = {...discovery.dataset, ...discovery.closest('[data-search-popup]')?.querySelector('predictive-search')?.dataset};
    arrivalsRequests.set(discovery, true);
    grid.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch('/api/2026-07/graphql.json', {
        method: 'POST', headers: {'Content-Type': 'application/json'}, signal: AbortSignal.timeout(15000),
        body: JSON.stringify({query: arrivalsQuery, variables: {handle: grid.dataset.availableArrivals, country: config.country || null, language: config.language || null}})
      });
      if (!response.ok) throw new Error('Arrivals unavailable');
      const result = await response.json();
      if (result.errors?.length || !result.data?.collection) throw new Error('Arrivals unavailable');
      const available = result.data.collection.products.nodes.filter(product => product.availableForSale);
      const focusedHref = grid.contains(document.activeElement) ? document.activeElement.closest('a')?.getAttribute('href') : null;
      grid.innerHTML = available.map(product => productMarkup(product, config)).join('');
      grid.dataset.productCount = String(available.length);
      discovery.dataset.availableChecked = String(Date.now());
      if (focusedHref) {
        const replacement = [...grid.querySelectorAll('a')].find(link => link.getAttribute('href') === focusedHref);
        (replacement || discovery.closest('[data-search-container]'))?.focus({preventScroll: true});
      }
    } catch (error) {
      // The Liquid fallback already contains only available products.
    } finally {
      grid.setAttribute('aria-busy', 'false');
      arrivalsRequests.delete(discovery);
    }
  }
  function productMarkup(product, config) {
    const href = `${(config.rootUrl || '/').replace(/\/?$/, '/')}products/${encodeURIComponent(product.handle)}`;
    const price = product.priceRange.minVariantPrice;
    const money = typeof window.formatMoney === 'function' && config.moneyFormat
      ? new DOMParser().parseFromString(window.formatMoney(Math.round(Number(price.amount) * 100), config.moneyFormat), 'text/html').body.textContent
      : new Intl.NumberFormat(document.documentElement.lang || 'it', {style: 'currency', currency: price.currencyCode}).format(Number(price.amount));
    let photo = '<span class="ra-search-product__placeholder" aria-hidden="true"></span>';
    if (product.featuredImage) {
      const url = new URL(product.featuredImage.url); url.searchParams.set('width', '360');
      photo = `<img src="${escapeHTML(url.href)}" width="270" height="360" alt="${escapeHTML(product.featuredImage.altText || product.title)}" loading="lazy">`;
    }
    return `<li><a class="ra-search-product" href="${escapeHTML(href)}" data-created-at="${escapeHTML(product.createdAt)}"><span class="ra-search-product__image">${photo}${product.availableForSale ? '' : `<span class="ra-search-product__badge">${escapeHTML(config.soldOutMessage)}</span>`}</span><span class="ra-search-product__title">${escapeHTML(product.title)}</span><span class="ra-search-product__price">${escapeHTML(money)}</span></a></li>`;
  }
  function markup(result, config) {
    return `<div class="ra-search-results" data-search-items-wrapper data-product-count="${result.nodes.length}" data-sort-order="created-descending">${result.nodes.length ? `<ul class="ra-search-products" role="list" aria-label="${escapeHTML(config.productsLabel)}">${result.nodes.map(product => productMarkup(product, config)).join('')}</ul>` : `<p class="ra-search-empty" role="status">${escapeHTML(config.emptyMessage)}</p>`}</div>`;
  }
  async function initPage(page) {
    if (page.dataset.initialized) return;
    page.dataset.initialized = 'true';
    const config = page.dataset;
    const params = new URLSearchParams(location.search);
    const term = params.get('q')?.trim() || '';
    const grid = page.querySelector('[data-newest-grid]');
    const status = page.querySelector('[data-newest-status]');
    const retry = page.querySelector('[data-newest-retry]');
    const navigation = page.querySelector('[data-newest-navigation]');
    const input = page.querySelector('[name="q"]');
    input.value = term;
    if (!term) { status.hidden = true; grid.setAttribute('aria-busy', 'false'); return; }
    async function load() {
      grid.setAttribute('aria-busy', 'true');
      status.textContent = config.loadingMessage; status.hidden = false; retry.hidden = true;
      try {
        const result = await products(term, config, {after: params.get('ra_after'), before: params.get('ra_before')});
        if (!result.nodes.length) { grid.replaceChildren(); status.textContent = config.emptyMessage; return; }
        // Render the existing full product cards through Liquid (12 handles, below all_products' limit).
        const url = new URL(config.searchUrl, location.origin);
        url.search = new URLSearchParams({q: result.nodes.map(product => product.handle).join('|'), type: 'product', section_id: 'ra-search-ordered-cards'}).toString();
        const response = await fetch(url, {signal: AbortSignal.timeout(15000)});
        if (!response.ok) throw new Error('Product cards unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const cards = [...html.querySelectorAll('[data-ordered-product]')];
        if (cards.length !== result.nodes.length) throw new Error('Incomplete product cards');
        const byHandle = new Map(cards.map(card => [card.dataset.orderedProduct, card]));
        const ordered = result.nodes.map(product => byHandle.get(product.handle));
        if (ordered.some(card => !card)) throw new Error('Missing product card');
        grid.replaceChildren(...ordered);
        status.hidden = true;
        const pageNumber = Math.max(1, Number.parseInt(params.get('ra_page'), 10) || 1);
        function setPageLink(link, enabled, cursor, direction, number) {
          link.hidden = !enabled;
          if (!enabled) return;
          const next = new URL(location.href);
          next.searchParams.delete('ra_after'); next.searchParams.delete('ra_before');
          next.searchParams.set(direction, cursor); next.searchParams.set('ra_page', String(number));
          link.href = next.href;
        }
        setPageLink(navigation.querySelector('[data-newest-previous]'), result.pageInfo.hasPreviousPage, result.pageInfo.startCursor, 'ra_before', pageNumber - 1);
        setPageLink(navigation.querySelector('[data-newest-next]'), result.pageInfo.hasNextPage, result.pageInfo.endCursor, 'ra_after', pageNumber + 1);
        navigation.querySelector('[data-newest-page-number]').textContent = `${config.pageLabel} ${pageNumber}`;
        navigation.hidden = !(result.pageInfo.hasNextPage || result.pageInfo.hasPreviousPage);
        window.MinimogTheme?.Wishlist?.setWishlistButtonsState();
        window.MinimogTheme?.CompareProduct?.setCompareButtonsState();
      } catch (error) {
        status.textContent = config.pageErrorMessage; status.hidden = false; retry.hidden = false;
      } finally { grid.setAttribute('aria-busy', 'false'); }
    }
    retry.addEventListener('click', load);
    load();
  }
  window.RaNewestSearch = {products, markup, refreshArrivals};
  const init = () => {
    document.querySelectorAll('[data-newest-search-page]').forEach(initPage);
    document.querySelectorAll('[data-ra-search-discovery]').forEach(refreshArrivals);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
